import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, catchError, finalize, switchMap, tap, throwError } from 'rxjs';
import { AuthService } from 'src/app/components/auth/service/auth.service';
import { Router } from '@angular/router';
import { SocketService } from '../socket-service/socket-service.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private router: Router,
    private socket: SocketService

  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skipUrls = [
      '/auth/refresh-tokens',
      '/auth/login'
    ];

    const isSkip = skipUrls.some(url => request.url.includes(url));

    if (isSkip) {
      return next.handle(request);
    }

    const accessToken = this.authService.getToken('access_token');

    if (accessToken) {
      request = this.addToken(request, accessToken);
    }

    if (!(request.body instanceof FormData)) {
      request = request.clone({
        headers: request.headers.set('Content-Type', 'application/json')
      });
    }

    return next.handle(request).pipe(
      catchError((error) => {
        // Check if access token is expired
        if (error.status === 401 && accessToken) {
          return this.handleTokenExpired(request, next);
        }

        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  private handleTokenExpired(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Call the refresh token endpoint to get a new access token
    return this.authService.refreshAccessToken().pipe(
      tap((response) => {
        if (response.access && response.access.token) {
          this.authService.setToken('access_token', response.access.token);
        }
        if (response.refresh && response.refresh.token) {
          this.authService.setToken('refresh_token', response.refresh.token);
        }
      }),
      switchMap(() => {
        const newAccessToken = this.authService.getToken('access_token');
        // Retry the original request with the new access token
        return next.handle(this.addToken(request, newAccessToken));
      }),
      catchError((error) => {
        // Handle refresh token error (e.g., redirect to login page)
        console.error('Error handling expired access token:', error);
        this.socket.disconnectSocket();
        this.authService.removeToken('access_token');
        this.authService.removeToken('refresh_token');
        this.router.navigateByUrl('/login');

        return throwError(() => error);
      })
    );
  }
}
